import { S } from './state.js';
import { BAL } from './config.js';
import { GEAR_AFFIXES, GEAR_ICONS, GEAR_NOUNS, GEAR_TIERS, LEGENDARY_UNIQUES, LUNAR_UNIQUES } from './data/gear.js';
import { GEAR_TAGS, TIERS } from './data/items.js';
import { ITEM_CAPPED, ITEM_TAGS, gearInteractionNote, prereqMet, weaponInteractionNote } from './data/weapons.js';
import { rnd, rrange } from './fx.js';
import { randomItemOfTier, randomMythic, rollBloodTier, rollShrineChoices, shrineItemPrice } from './economy.js';
import { player } from './entities.js';
import { clampToWorld, pushOutOfRocks, worldLimA } from './world.js';
import { ctx } from './canvas.js';
import { applyStatCard, buyShrineDraft, closeShrineDraft, rollStatCards, takeBossReward, takeLevelUp, tryOpenLevelUp } from './shrines.js';
import { startWave } from './combat.js';
import { roundRect } from './screens.js';
import { recomputeStats } from './stats.js';
// ---- Gear generation & shop logic (data lives in the config section up top) ----
export function gearTier() {
  let r = rnd();
  for (const t in GEAR_TIERS) { if (r < GEAR_TIERS[t].weight) return t; r -= GEAR_TIERS[t].weight; }
  return 'common';
}
export function makeGear(tier) {
  const naff = tier === 'common' ? 1 : tier === 'lunar' ? 1 : tier === 'uncommon' ? 2 : 2;
  const stats = {}, lines = [];
  const pool = GEAR_AFFIXES.slice();
  for (let i = 0; i < naff && pool.length; i++) {
    const a = pool.splice(Math.floor(rnd() * pool.length), 1)[0];
    const v = Math.round(rrange(a.base[0], a.base[1]) * GEAR_TIERS[tier].mul);
    stats[a.key] = (stats[a.key] || 0) + v;
    lines.push(a.fmt(v));
  }
  let unique = null;
  // uniques that are ⚠-dead on the held weapon (Ricochet on Beam, forks on Arc…) don't roll (0.19,
  // same rule as items: shops don't show what you can't use). The stat affixes always work, so a
  // dead unique just falls back to another pick — never an empty shop.
  const _uniquePick = pool2 => {
    const ok = pool2.filter(u => { const n = gearInteractionNote(u.key); return !(n && n[0] === '⚠'); });
    const p = ok.length ? ok : pool2;
    return p[Math.floor(rnd() * p.length)];
  };
  if (tier === 'legendary') unique = _uniquePick(LEGENDARY_UNIQUES);
  else if (tier === 'lunar') unique = _uniquePick(LUNAR_UNIQUES);
  return {
    tier, stats, lines,
    unique: unique ? unique.key : null,
    uniqueDesc: unique ? unique.desc : null,
    name: unique ? unique.name : GEAR_NOUNS[Math.floor(rnd() * GEAR_NOUNS.length)],
    icon: unique ? unique.icon : GEAR_ICONS[Math.floor(rnd() * GEAR_ICONS.length)],
    price: 0, sold: false,   // wave-shop gear is free to take
  };
}
export function rollShopOffer() { S.shopOffer = rollStatCards(3); }   // the wave shop offers 3 rarity-rolled STAT cards (same model as level-ups; no gear/uniques)
export function openShop() {
  S.shopOpen = true; S.rerollsThisShop = 0; S.shopPicked = false;
  S.rerollCost = Math.round(18 + S.waveNum * 2);
  S.freeRerolls = (S.nextShopBoon ? 1 : 0) + (S.fervorTier >= 3 ? 1 : 0) + (S.items.architect ? 1 : 0);   // cleared the wave / on fire / Architect's Pen → free spins
  S.nextShopBoon = false;
  rollShopOffer();
}
// the price of the NEXT shop reroll: the escalating base, floored at a % of current wealth (0.16) —
// same medicine as the gamble shrine. A rich run could fish legendaries for pocket change (40k held
// vs a 38-coin reroll at w10); now every pull costs at least 6% of what you're holding.
export function shopRerollCost() { return Math.round(Math.max(S.rerollCost, S.coinsRun * 0.06) * Math.max(0.6, 1 - 0.08 * (S.items.hagglers || 0))); }   // Haggler's Charm trims the spin price too
export function rerollShop() {
  if (S.shopPicked) return;                             // reroll only before you pick
  if (S.freeRerolls > 0) { S.freeRerolls--; rollShopOffer(); return; }   // spend a free reroll first
  const cost = shopRerollCost();
  if (S.coinsRun < cost) return;
  S.coinsRun -= cost; S.rerollsThisShop++;
  S.rerollCost = Math.round(S.rerollCost * 1.6);
  rollShopOffer();
}
export function buyGear(i) {   // take ONE free stat card, then advance into the next wave
  if (S.shopPicked) return;
  const c = S.shopOffer[i]; if (!c) return;
  S.shopPicked = true;
  applyStatCard(c);
  closeShop();
}
export function grantGear(g) {                               // append to the unbounded collection
  const oldMax = S.MAX_HP;
  S.equipment.push(g);
  recomputeStats();
  player.hp += Math.max(0, S.MAX_HP - oldMax);          // gaining max HP heals you for it
}
export function closeShop() {
  S.shopOpen = false;
  player.iFrames = Math.max(player.iFrames, 50);        // brief grace coming out of the shop
  startWave(++S.waveNum); S.waveTimer = S.WAVE_INTERVAL;      // proceed into the next wave
  tryOpenLevelUp();                                     // surface any level-ups that were held back during the shop
}

// spawn shrines around the player to walk into; cull far stuff
export function ensureWorld() {
  // the Vault: ONE per run, from wave 100 — deep-run territory only (2 of 43 runs in 0.15). A wave-8
  // vault made mythics a fixed, farmable purchase; now boss drafts & blood pacts are the only realistic
  // doors, and the Vault is the trophy case for runs that have already conquered the game.
  if (!S.vaultSpawned && S.waveNum >= BAL.mythic.vaultWave) {
    S.vaultSpawned = true;
    const va = rrange(0, Math.PI * 2), vd = worldLimA(va) * rrange(0.7, 0.85);
    const vp = { x: Math.cos(va) * vd, y: Math.sin(va) * vd }; pushOutOfRocks(vp, 70);
    S.shrines.push({ x: vp.x, y: vp.y, kind: 'vault', blood: false, item: null, choices: null, taken: false, ph: rrange(0, 6.28) });
  }
  if (--S.shrineTimer <= 0) {
    if (S.shrines.length >= BAL.shrines.cap) { S.shrineTimer = 120; return; }   // map's full — fresh pods wait until you TAKE some (shrines persist now)
    // keep shrines APART (0.14): overlapping pods made it impossible to pick the one you wanted
    let x = 0, y = 0;
    for (let t = 0; t < 8; t++) {
      const ang = rrange(0, Math.PI * 2), rad = rrange(220, 460);
      const wp2 = { x: player.x + Math.cos(ang) * rad, y: player.y + Math.sin(ang) * rad };
      clampToWorld(wp2, 80); pushOutOfRocks(wp2, 70);   // reachable: inside the walls, never inside a rock
      x = wp2.x; y = wp2.y;
      let clear = true;
      for (const o of S.shrines) if (!o.taken && Math.hypot(o.x - x, o.y - y) < 160) { clear = false; break; }
      if (clear) break;   // last attempt keeps its spot regardless — a crowded shrine beats no shrine
    }
    const roll = rnd();
    let kind = 'item';
    if (S.waveNum >= 4 && roll < 0.04) kind = 'pandora';            // ~4% (0.14: rarer but reworked — selective reroll) — from wave 4
    else if (S.waveNum >= 3 && roll < 0.23) kind = 'reroll';        // ~15% — Gambler's Shrine (from wave 3)
    else if (S.waveNum >= 2 && rnd() < 0.3) kind = 'blood';         // ~30% of the rest — HP-cost god-run gamble
    // per-kind caps (shrines persist now): a player who never gambles shouldn't watch dice & boxes
    // squat the 12 slots until caches stop spawning — at its cap, a kind falls through to an item cache
    const kindCount = {};
    for (const s2 of S.shrines) kindCount[s2.kind] = (kindCount[s2.kind] || 0) + 1;
    if ((kind === 'pandora' && (kindCount.pandora || 0) >= BAL.shrines.maxPandora) ||
        (kind === 'reroll'  && (kindCount.reroll  || 0) >= BAL.shrines.maxReroll) ||
        (kind === 'blood'   && (kindCount.blood   || 0) >= BAL.shrines.maxBlood)) kind = 'item';
    let item = null, choices = null;
    if (kind === 'blood') item = (rnd() < BAL.mythic.bloodOdds ? randomMythic() : null) || randomItemOfTier(rollBloodTier());   // a few blood pacts offer a MYTHIC
    else if (kind === 'item') {
      choices = rollShrineChoices();
      item = choices.reduce((b, c) => TIERS[c.tier].mult > TIERS[b.tier].mult ? c : b, choices[0]);   // best rarity drives the cache's color/minimap blip
    }
    S.shrines.push({ x, y, kind, blood: kind === 'blood', item, choices, taken: false, ph: rrange(0, 6.28) });
    S.shrineTimer = Math.floor(rrange(360, 640));
  }
  // shrines PERSIST (0.16): the world is a finite map now — pods are landmarks you can route back to,
  // not streamed content. Only taken ones leave; the spawn cap above stops unbounded buildup.
  S.shrines = S.shrines.filter(s => !s.taken);
  S.coins = S.coins.filter(c => !c.got && Math.hypot(c.x - player.x, c.y - player.y) < 1900);
  S.pickups = S.pickups.filter(p => !p.got && Math.hypot(p.x - player.x, p.y - player.y) < 1900);
}

// ============================ AUTO-SHOP (0.18) ============================
// When ON, the four "pick a reward" menus resolve themselves: the wave shop, item caches, level-ups
// and boss drafts. Blood shrines, Pandora and the gamble shrine stay MANUAL — they're destructive
// bets, not picks. The chosen card is ringed for ~0.75s before the pick fires so the player sees
// (and can disagree with — any manual pick still works during the countdown) what the robot wants.
S.autoShopT = 0, S.autoPickIndex = -1, S.autoCur = '';   // countdown · planned pick (overlays ring it) · which menu instance the timer belongs to
export function scoreItemOffer(it) {   // shared heuristic: rarity × build fit, discounted when capped/inert/anti-weapon
  let s = TIERS[it.tier].mult;
  const t = ITEM_TAGS[it.key];
  if (t && t === S.buildTag) s *= 2.0;
  else if (t && S.arch[t] > 0) s *= 1.3;
  if (ITEM_CAPPED[it.key] && ITEM_CAPPED[it.key]()) s *= 0.05;
  else if (!prereqMet(it.key)) s *= 0.25;
  const note = weaponInteractionNote(it.key);
  if (note && note[0] === '⚠') s *= 0.3;
  return s;
}
export function scoreGearOffer(g) {
  let s = (GEAR_TIERS[g.tier] ? GEAR_TIERS[g.tier].mul : 1) + Object.keys(g.stats || {}).length * 0.25;
  if (g.unique && GEAR_TAGS[g.unique] && GEAR_TAGS[g.unique] === S.buildTag) s *= 1.8;
  return s;
}
const _STAT_W = { dmg: 3, fire: 2.5, multishot: 4, pierce: 2.5, bounce: 2, critdmg: 1.5, elite: 1.5, luck: 1.5, hp: 2, speed: 1.5, exp: 1.2, crit: 1, armor: 1, regen: 1, thorns: 1, size: 1, bspeed: 1, range: 0.8, greed: 0.8 };
export function scoreStatCard(c) {   // auto-pick heuristic for stat-card drafts (shop + level-up): sum the bundle, build-aware
  let s = 0;
  for (const m of c.mods) {
    let w = _STAT_W[m.key] || 1;
    if (S.buildTag === 'crit' && (m.key === 'crit' || m.key === 'critdmg')) w *= 2;
    if (S.buildTag === 'tank' && (m.key === 'hp' || m.key === 'armor' || m.key === 'regen' || m.key === 'thorns')) w *= 2;
    if (m.key === 'hp' && player.hp < S.MAX_HP * 0.4) w *= 1.5;
    s += m.amt < 0 ? -w : w;
  }
  return s;
}
export function autoResolveMenus() {
  S.autoPickIndex = -1;
  if (!S.save.settings.autoShop || !S.started || S.dead || S.paused || S.showJournal || S.bloodConfirm || S.pandoraConfirm) { S.autoShopT = 0; S.autoCur = ''; return; }
  let key = null, pick = -1;
  if (S.shopOpen) {
    if (!S.shopPicked) {   // the one free pick: best-scoring stat card (no auto-rerolls — coins are the player's)
      key = 'shop'; let bs = -1;
      S.shopOffer.forEach((c, i) => { const s = scoreStatCard(c); if (s > bs) { bs = s; pick = i; } });
    } else key = 'shopdone';   // picked (by us or the player) — close after a beat
  } else if (S.shrineDraft) {
    key = 'cache'; let bs = 0;
    S.shrineDraft.s.choices.forEach((it, i) => {
      const price = shrineItemPrice(it);
      if (price > S.coinsRun) return;                                              // can't afford
      if (price > S.coinsRun * 0.35 && TIERS[it.tier].mult < TIERS.epic.mult) return;   // don't blow the bank on small stuff
      const s = scoreItemOffer(it);
      if (s > bs) { bs = s; pick = i; }
    });   // pick stays -1 → walk away
  } else if (S.levelUp) {
    key = 'level:' + S.level + ':' + S.pendingLevels;   // chained drafts re-arm the countdown
    let bs = -1;
    S.levelUp.choices.forEach((c, i) => { const s = scoreStatCard(c); if (s > bs) { bs = s; pick = i; } });
  } else if (S.bossReward) {
    key = 'boss'; let bs = -1;
    S.bossReward.choices.forEach((c, i) => {
      const s = c.kind === 'gear' ? scoreGearOffer(c.gear)
              : c.kind === 'item' ? scoreItemOffer(c.item)
              : 1.2 + (player.hp < S.MAX_HP * 0.4 ? 2.5 : 0);   // Windfall: mostly the full heal's value
      if (s > bs) { bs = s; pick = i; }
    });   // never auto-rerolls
  } else { S.autoShopT = 0; S.autoCur = ''; return; }
  if (key !== S.autoCur) { S.autoCur = key; S.autoShopT = key === 'shopdone' ? 30 : 45; }
  S.autoPickIndex = pick;
  if (--S.autoShopT > 0) return;
  S.autoCur = ''; S.autoPickIndex = -1;
  if (key === 'shop')           { if (pick >= 0) buyGear(pick); else closeShop(); }
  else if (key === 'shopdone')  closeShop();
  else if (key === 'cache')     { if (pick >= 0) buyShrineDraft(pick); else closeShrineDraft(); }
  else if (key.slice(0, 5) === 'level') takeLevelUp(Math.max(0, pick));
  else if (key === 'boss')      takeBossReward(Math.max(0, pick));
}
// the animated "robot wants this one" ring — each overlay calls it per card with the card's index
export function autoRing(i, x, y, w, h) {
  if (!S.save.settings.autoShop || i !== S.autoPickIndex) return;
  ctx.save();
  ctx.strokeStyle = '#7dffb0'; ctx.lineWidth = 2.5;
  ctx.setLineDash([10, 6]); ctx.lineDashOffset = -S.frame * 0.5;
  roundRect(x - 5, y - 5, w + 10, h + 10, 14); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#7dffb0'; ctx.font = 'bold 11px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('🤖 auto-pick ' + Math.ceil(S.autoShopT / 60 * 10) / 10 + 's', x + w / 2, y - 14);
  ctx.restore();
}
